/* eslint-disable no-underscore-dangle */
import React, { useContext } from 'react';

import * as CMSContent from '@swf/cms';
import ErrorBoundary from '@swf/components/ErrorBoundary/ErrorBoundary';
import StarsWidget from '@swf/components/StarsWidget/StarsWidget';
import widgets from '@swf/widgets/index';

import { CMSContext } from '../CMSContext/CMSContext';

const CMSReference: React.FC<CMSContent.Reference | CMSContent.GlobalFieldReference> = (
  reference
) => {
  const content = useContext(CMSContext);
  const keys = Object.keys(reference);
  let component = null;

  if (keys.length === 1) {
    const [type] = keys;
    const data = reference[type];

    switch (type) {
      case 'html':
        component = <CMSContent.CMSHTML {...data} />;
        break;
      case 'brightcove_video':
        component = <CMSContent.CMSBrightcoveVideo {...data} />;
        break;
      case 'cta':
        component = <CMSContent.CMSCTA {...data} />;
        break;
      case 'client_stats':
        component = <CMSContent.CMSClientState {...data} />;
        break;
      case 'deep_link':
        component = <CMSContent.CMSDeepLink {...data} />;
        break;
      case 'device_based_content':
        component = <CMSContent.CMSDeviceBasedContent {...data} />;
        break;
      case 'heading':
        component = <CMSContent.CMSHeading {...data} />;
        break;
      case 'image':
        component = <CMSContent.CMSImage {...data} />;
        break;
      case 'link':
        component = <CMSContent.CMSLink {...data} />;
        break;
      case 'linked_image':
        component = <CMSContent.CMSLinkedImage {...data} />;
        break;
      case 'notice':
        component = <CMSContent.CMSNotice {...data} />;
        break;
      case 'picture':
        component = <CMSContent.CMSPicture {...data} />;
        break;
      case 'twitter':
        component = <CMSContent.CMSTwitter {...(data as CMSContent.Twitter)} />;
        break;
      case 'twitch':
        component = <CMSContent.CMSTwitch {...(data as CMSContent.Twitch)} />;
        break;
      case 'xml_feed':
        component = <CMSContent.CMSXMLFeed {...(data as CMSContent.XMLFeed)} />;
        break;
    }

    return <ErrorBoundary>{component}</ErrorBoundary>;
  }

  const { uid, _content_type_uid } = reference as CMSContent.Reference;
  const data = content[uid];
  if (!data) return null;

  if (Object.keys(widgets).includes(_content_type_uid)) {
    component = <StarsWidget {...data} />;
  } else {
    switch (_content_type_uid) {
      case 'accordion':
        component = <CMSContent.CMSAccordion {...(data as CMSContent.Accordion)} />;
        break;
      case 'bonus_grid':
        component = <CMSContent.CMSBonusGrid {...(data as CMSContent.BonusGrid)} />;
        break;
      case 'box':
        component = <CMSContent.CMSBox {...(data as CMSContent.Box)} />;
        break;
      case 'carousel':
        component = <CMSContent.CMSCarousel {...(data as CMSContent.Carousel)} />;
        break;
      case 'category_highlight':
        component = <CMSContent.CMSCategoryHighlight {...(data as CMSContent.CategoryHighlight)} />;
        break;
      case 'content_area':
        component = <CMSContent.CMSContentArea {...(data as CMSContent.ContentArea)} />;
        break;
      case 'content_card':
        component = <CMSContent.CMSContentCard {...(data as CMSContent.ContentCard)} />;
        break;
      case 'cta_grid':
        component = <CMSContent.CMSCTAGrid {...(data as CMSContent.CTAGrid)} />;
        break;
      case 'details_grid':
        component = <CMSContent.CMSDetailsGrid {...(data as CMSContent.DetailsGrid)} />;
        break;
      case 'event_schedule':
        component = <CMSContent.CMSEventSchedule {...(data as CMSContent.EventSchedule)} />;
        break;
      case 'event_tournament':
        component = <CMSContent.CMSEventTournament {...(data as CMSContent.EventTournament)} />;
        break;
      case 'faq':
        component = <CMSContent.CMSFAQ {...(data as CMSContent.FAQ)} />;
        break;
      case 'form':
        component = <CMSContent.CMSForm {...(data as CMSContent.Form)} />;
        break;
      case 'grid':
        component = <CMSContent.CMSGrid {...(data as CMSContent.Grid)} />;
        break;
      case 'icons_grid':
        component = <CMSContent.CMSIconsGrid {...(data as CMSContent.IconsGrid)} />;
        break;
      case 'leader_board':
        component = <CMSContent.CMSLeaderBoard {...(data as CMSContent.LeaderBoard)} />;
        break;
      case 'linked_image_list':
        component = <CMSContent.CMSLinkedImageList {...(data as CMSContent.LinkedImageList)} />;
        break;
      case 'main':
        component = <CMSContent.CMSMain {...(data as CMSContent.Main)} />;
        break;
      case 'glossary':
        component = <CMSContent.CMSGlossary {...(data as CMSContent.Glossary)} />;
        break;
      case 'highlight':
        component = <CMSContent.CMSHighlight {...(data as CMSContent.Highlight)} />;
        break;
      case 'menu':
        component = <CMSContent.CMSContextMenu {...{ menu: data as CMSContent.Menu }} />;
        break;
      case 'media_grid':
        component = <CMSContent.CMSMediaGrid {...(data as CMSContent.MediaGrid)} />;
        break;
      case 'page':
        component = <CMSContent.CMSPage {...(data as CMSContent.Page)} />;
        break;
      case 'panel':
        component = <CMSContent.CMSPanel {...(data as CMSContent.Panel)} />;
        break;
      case 'payment_vendors':
        component = <CMSContent.CMSPaymentVendors {...(data as CMSContent.PaymentVendors)} />;
        break;
      case 'podcast':
        component = <CMSContent.CMSPodcast {...(data as CMSContent.Podcast)} />;
        break;
      case 'promotion_banner':
        component = <CMSContent.CMSPromotionBanner {...(data as CMSContent.PromotionBanner)} />;
        break;
      case 'refer_a_friend':
        component = <CMSContent.CMSReferAFriend {...(data as CMSContent.ReferAFriend)} />;
        break;
      case 'seo':
        component = <CMSContent.CMSSEO {...(data as CMSContent.SEO)} />;
        break;
      case 'side':
        component = <CMSContent.CMSSide {...(data as CMSContent.Side)} />;
        break;
      case 'side_cta':
        component = <CMSContent.CMSSideCTA {...(data as CMSContent.SideCTA)} />;
        break;
      case 'side_related_content':
        component = (
          <CMSContent.CMSSideRelatedContent {...(data as CMSContent.SideRelatedContent)} />
        );
        break;
      case 'side_twitter':
        component = <CMSContent.CMSSideTwitter {...(data as CMSContent.SideTwitter)} />;
        break;
      case 'site_header':
        component = <CMSContent.CMSHeader {...(data as CMSContent.SiteHeader)} />;
        break;
      case 'site_footer':
        component = <CMSContent.CMSFooter {...(data as CMSContent.SiteFooter)} />;
        break;
      case 'sticky_footer':
        component = <CMSContent.CMSStickyFooter {...(data as CMSContent.StickyFooter)} />;
        break;
      case 'terms':
        component = <CMSContent.CMSTerms {...(data as CMSContent.Terms)} />;
        break;
      case 'top':
        component = <CMSContent.CMSTop {...(data as CMSContent.Top)} />;
        break;
      case 'tiles_grid':
        component = <CMSContent.CMSTilesGrid {...(data as CMSContent.TilesGrid)} />;
        break;
      case 'tabs':
        component = <CMSContent.CMSTabs {...(data as CMSContent.Tabs)} />;
        break;
      case 'video_panel':
        component = <CMSContent.CMSVideoPanel {...(data as CMSContent.VideoPanel)} />;
        break;
    }
  }

  return <ErrorBoundary>{component}</ErrorBoundary>;
};

export default CMSReference;
